import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Gift, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BACKEND_URL } from "@/lib/api";

const OCCASIONS = [
  { value: "just-because", label: "Just because" },
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
  { value: "valentine", label: "Valentine's" },
  { value: "bachelorette", label: "Bachelorette" },
  { value: "wedding", label: "Wedding" },
  { value: "housewarming", label: "Housewarming" },
];

const RECIPIENTS = [
  { value: "partner", label: "For a partner" },
  { value: "self", label: "For myself" },
  { value: "couple", label: "For a couple" },
  { value: "friend", label: "For a friend" },
];

const BUDGET_PRESETS = [75, 150, 250, 500];
const BUNDLE_SIZES = [1, 3, 5];

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const GiftConcierge = () => {
  const { addItem, setOpen: openCart } = useCart();
  const [budget, setBudget] = useState(150);
  const [vibe, setVibe] = useState("");
  const [occasion, setOccasion] = useState("just-because");
  const [recipient, setRecipient] = useState("partner");
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (vibe.trim().length < 3) {
      toast.error("Tell us the vibe first — a few words is plenty.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/gift-concierge/recommend", {
        budget: Number(budget),
        vibe: vibe.trim(),
        occasion,
        recipient,
        count,
      });
      setResult(data);
      // Scroll into view after render
      setTimeout(() => {
        document
          .getElementById("gift-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The house couldn't compose a bundle. Try a different vibe.");
    } finally {
      setLoading(false);
    }
  };

  const addAllToCart = () => {
    if (!result?.products?.length) return;
    result.products.forEach((p) => addItem(p, 1));
    toast.success("Bundle sent to your bag — the house has wrapped it in shadow.");
    openCart(true);
  };

  return (
    <div data-testid="gift-concierge-page" className="grain min-h-screen">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-8 max-w-6xl">
        <div className="overline mb-4 flex items-center gap-2">
          <Sparkles size={12} strokeWidth={1.5} /> The Gift Concierge
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-bone leading-[0.95]">
          A bespoke bundle,
          <br />
          <em className="not-italic" style={{ color: "var(--rs-terracotta)" }}>
            wrapped in shadow.
          </em>
        </h1>
        <p className="text-muted-bone max-w-2xl mt-6 leading-relaxed">
          Tell us who it&apos;s for, what you&apos;d like to spend, and the mood you&apos;re chasing.
          The house will assemble the pieces — quietly, tastefully, discreetly.
        </p>
      </section>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-16 pb-16 max-w-6xl">
        <form
          data-testid="gift-form"
          onSubmit={submit}
          className="border p-8 md:p-12 space-y-10"
          style={{ borderColor: "var(--rs-border)", background: "var(--rs-surface)" }}
        >
          {/* Vibe */}
          <div>
            <label className="overline block mb-3">The vibe</label>
            <textarea
              data-testid="gift-vibe-input"
              value={vibe}
              onChange={(e) => setVibe(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="e.g. slow anniversary night · playful bachelorette prelude · quiet self-care ritual"
              className="w-full bg-transparent border px-4 py-3 text-bone placeholder-muted-bone/50 outline-none resize-none font-serif-display text-xl leading-snug"
              style={{ borderColor: "var(--rs-border)" }}
            />
            <div className="text-right text-[10px] uppercase tracking-[0.22em] text-muted-bone mt-1">
              {vibe.length}/280
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="overline block mb-4">
              Budget — <span className="text-bone">${budget}</span>
            </label>
            <input
              data-testid="gift-budget-slider"
              type="range"
              min={30}
              max={800}
              step={5}
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              className="w-full accent-[var(--rs-oxblood)]"
            />
            <div className="flex flex-wrap gap-2 mt-4">
              {BUDGET_PRESETS.map((b) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`gift-budget-preset-${b}`}
                  onClick={() => setBudget(b)}
                  className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 transition"
                  style={{
                    color: budget === b ? "var(--rs-bone)" : "var(--rs-terracotta)",
                    background: budget === b ? "var(--rs-oxblood)" : "transparent",
                    border: `1px solid ${budget === b ? "var(--rs-oxblood)" : "rgba(192,112,80,0.35)"}`,
                  }}
                >
                  ${b}
                </button>
              ))}
            </div>
          </div>

          {/* Occasion + Recipient */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-3">Occasion</label>
              <select
                data-testid="gift-occasion-select"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="w-full bg-transparent border px-4 py-3 text-bone outline-none"
                style={{ borderColor: "var(--rs-border)" }}
              >
                {OCCASIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-black">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-3">Recipient</label>
              <select
                data-testid="gift-recipient-select"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="w-full bg-transparent border px-4 py-3 text-bone outline-none"
                style={{ borderColor: "var(--rs-border)" }}
              >
                {RECIPIENTS.map((r) => (
                  <option key={r.value} value={r.value} className="bg-black">
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bundle size */}
          <div>
            <label className="overline block mb-4">Bundle size</label>
            <div className="flex gap-3">
              {BUNDLE_SIZES.map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`gift-count-${n}`}
                  onClick={() => setCount(n)}
                  className="flex-1 py-4 uppercase text-xs tracking-[0.22em] transition"
                  style={{
                    color: count === n ? "var(--rs-bone)" : "var(--rs-terracotta)",
                    background: count === n ? "var(--rs-oxblood)" : "transparent",
                    border: `1px solid ${count === n ? "var(--rs-oxblood)" : "rgba(192,112,80,0.35)"}`,
                  }}
                >
                  {n} {n === 1 ? "Piece" : "Pieces"}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            data-testid="gift-submit-btn"
            disabled={loading}
            className="w-full py-5 uppercase text-xs tracking-[0.28em] text-bone hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-3"
            style={{ background: "var(--rs-oxblood)" }}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={16} strokeWidth={1.5} />
                Composing the bundle…
              </>
            ) : (
              <>
                <Gift size={16} strokeWidth={1.5} />
                Compose my bundle
              </>
            )}
          </button>
        </form>
      </section>

      {/* Result */}
      {result && (
        <section
          id="gift-result"
          data-testid="gift-result"
          className="px-6 md:px-12 lg:px-16 pb-24 max-w-6xl"
        >
          <div
            className="border p-8 md:p-12"
            style={{ borderColor: "var(--rs-border)", background: "var(--rs-surface-2, #141010)" }}
          >
            <div className="overline mb-3">The House Suggests</div>
            <p
              data-testid="gift-blurb"
              className="font-serif-display text-2xl md:text-3xl text-bone leading-snug italic max-w-3xl"
            >
              &ldquo;{result.blurb}&rdquo;
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {result.tags?.map((t) => (
                <span
                  key={t}
                  className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5"
                  style={{
                    color: "var(--rs-terracotta)",
                    border: "1px solid rgba(192,112,80,0.35)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>

            {/* Products grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {result.products.map((p) => (
                <Link
                  key={p.product_id}
                  to={`/product/${p.slug}`}
                  data-testid={`gift-pick-${p.slug}`}
                  className="block group border"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  <div
                    className="aspect-[3/4] overflow-hidden"
                    style={{ background: "var(--rs-surface-2, #1a1414)" }}
                  >
                    <img
                      src={resolveImg(p.image)}
                      alt={p.name}
                      className="w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="overline">{p.category}</div>
                    <div className="font-serif-display text-lg text-bone mt-2 leading-snug">
                      {p.name}
                    </div>
                    <div className="text-muted-bone text-sm mt-2">${p.price.toFixed(2)}</div>
                  </div>
                </Link>
              ))}
            </div>

            {result.products.length === 0 && (
              <div
                data-testid="gift-empty-notice"
                className="mt-10 py-16 text-center border"
                style={{ borderColor: "var(--rs-border)" }}
              >
                <p className="font-serif-display text-2xl text-bone italic max-w-lg mx-auto">
                  Nothing in the house fits under ${result.budget.toFixed(0)} tonight.
                </p>
                <p className="text-muted-bone mt-3 text-sm">
                  Lift the budget a touch and we&apos;ll try again.
                </p>
              </div>
            )}

            {result.count_delivered < result.count_requested && result.count_delivered > 0 && (
              <p
                data-testid="gift-short-notice"
                className="text-xs uppercase tracking-[0.22em] text-muted-bone mt-8 italic"
              >
                Only {result.count_delivered} of {result.count_requested} pieces fit under
                ${result.budget}. Lift the budget for a fuller bundle.
              </p>
            )}

            {/* Total + CTA */}
            {result.products.length > 0 && (
              <div
                className="mt-10 pt-8 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-6"
                style={{ borderColor: "var(--rs-border)" }}
              >
                <div>
                  <div className="overline mb-1">Bundle total</div>
                  <div className="font-serif-display text-4xl text-bone">
                    ${result.total.toFixed(2)}
                  </div>
                  {result.under_budget_by > 0 && (
                    <div
                      data-testid="gift-under-budget"
                      className="text-xs uppercase tracking-[0.22em] text-muted-bone mt-2"
                    >
                      ${result.under_budget_by.toFixed(2)} under your budget
                    </div>
                  )}
                </div>
                <button
                  data-testid="gift-add-all-btn"
                  onClick={addAllToCart}
                  className="px-10 py-5 uppercase text-xs tracking-[0.28em] text-bone hover:opacity-90 transition"
                  style={{ background: "var(--rs-oxblood)" }}
                >
                  Add all to bag
                </button>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default GiftConcierge;
